/**
 * 261030 B2 (hợp đồng Rev 6) — «việc khác đang chạy trên plan» chặn thao tác của khung 4 bước. CHỈ logic thuần.
 * `workflow-status.blockingJobs[]` + 409 `WORKFLOW_RUN_ACTIVE` (kèm `blocking[]`) dùng chung một kiểu và một băng chữ.
 */
export interface BlockingJob {
  id: string;
  type: string;
  phase?: string | null;
  startedAt?: string | null;
  runningMinutes: number;
  maxMinutes: number;
  owner?: 'THIS_PROCESS' | 'OTHER_PROCESS' | 'UNKNOWN';
  /** Lần hoạt động cuối (heartbeat của runner bước, hoặc mốc cập nhật); null = không có. */
  lastActivityAt?: string | null;
  /** `true` ⇒ được mời «Gỡ việc treo» (có bằng chứng treo); `false` = việc đang chạy bình thường. */
  releasable: boolean;
  /** Vì sao được mời gỡ (Rev 7): OWNER_DEAD chủ tiến trình đã tắt · PAST_TTL quá thời hạn · NO_HEARTBEAT không có tín hiệu. */
  releasableReason?: 'OWNER_DEAD' | 'PAST_TTL' | 'NO_HEARTBEAT' | null;
}
const REASONS = ['OWNER_DEAD', 'PAST_TTL', 'NO_HEARTBEAT'] as const;

const asJob = (raw: any): BlockingJob | null => {
  if (!raw || typeof raw !== 'object' || typeof raw.id !== 'string' || !raw.id || typeof raw.type !== 'string') return null;
  const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  return { id: raw.id, type: raw.type, phase: typeof raw.phase === 'string' ? raw.phase : null, startedAt: typeof raw.startedAt === 'string' ? raw.startedAt : null,
    runningMinutes: num(raw.runningMinutes), maxMinutes: num(raw.maxMinutes), owner: raw.owner,
    lastActivityAt: typeof raw.lastActivityAt === 'string' ? raw.lastActivityAt : null, releasable: raw.releasable === true,
    releasableReason: (REASONS as readonly unknown[]).includes(raw.releasableReason) ? raw.releasableReason : null };
};
/** `blockingJobs` của status (hoặc `blocking[]` của lỗi) ⇒ danh sách sạch; phần tử hỏng bị bỏ. */
export const blockingJobsOf = (raw: unknown): BlockingJob[] => (Array.isArray(raw) ? raw.map(asJob).filter((j): j is BlockingJob => !!j) : []);

/** «FILM_STORYBOARD/compose». */
export const blockingNameOf = (job: Pick<BlockingJob, 'type' | 'phase'>): string => (job.phase ? `${job.type}/${job.phase}` : job.type);
/** «Đang có việc khác chạy: FILM_STORYBOARD/compose (đã 3 phút, tối đa 30)». */
export function blockingLineOf(job: BlockingJob): string {
  const limit = job.maxMinutes > 0 ? `, tối đa ${job.maxMinutes}` : '';
  return `Đang có việc khác chạy: ${blockingNameOf(job)} (đã ${job.runningMinutes} phút${limit})`;
}
/** Gộp việc từ status và từ lỗi 409 (trùng id lấy bản status). */
export function mergeBlockingJobs(fromStatus: BlockingJob[], fromError: BlockingJob[]): BlockingJob[] {
  const seen = new Set(fromStatus.map((j) => j.id));
  return [...fromStatus, ...fromError.filter((j) => !seen.has(j.id))];
}
/** 409 `WORKFLOW_RUN_ACTIVE` ⇒ việc chặn (`extra.blocking[]` hoặc `blocking[]` ở cấp ngoài, vì lỗi có dạng `{ code, message, ...extra }`); không phải mã đó ⇒ null. */
export function blockingFromErrorOf(err: any): { jobs: BlockingJob[]; message: string | null } | null {
  const d = err?.response?.data;
  if ((d?.code ?? d?.errorCode) !== 'WORKFLOW_RUN_ACTIVE') return null;
  const jobs = blockingJobsOf(d?.extra?.blocking ?? d?.blocking);
  return { jobs, message: typeof d?.message === 'string' && d.message.trim() ? d.message.trim() : null };
}
export const blockingErrorTextOf = (info: { jobs: BlockingJob[]; message: string | null }): string =>
  info.message ?? (info.jobs.length ? `${blockingLineOf(info.jobs[0])} — nếu đã treo, gỡ việc treo rồi chạy tiếp.` : 'Máy đang chạy một việc khác trên plan này — đợi xong rồi thử lại.');
/** Chữ hộp xác nhận «Gỡ việc treo». */
export const RELEASE_JOB_TITLE = 'Gỡ việc treo?';
/** Số phút từ `iso` tới `now` (làm tròn xuống, ≥ 0); chuỗi hỏng/rỗng ⇒ null. */
const minutesAgoOf = (iso: string | null | undefined, now: number): number | null => {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / 60000)) : null;
};
/** Lý do mời gỡ bằng tiếng Việt: «chủ tiến trình đã tắt» / «quá thời hạn (tối đa N phút)» / «không có tín hiệu N phút». Việc không releasable ⇒ null. */
export function releaseReasonOf(job: BlockingJob, now: number = Date.now()): string | null {
  if (job.releasableReason === 'OWNER_DEAD') return 'chủ tiến trình đã tắt';
  if (job.releasableReason === 'PAST_TTL') return job.maxMinutes > 0 ? `quá thời hạn (tối đa ${job.maxMinutes} phút)` : 'quá thời hạn';
  if (job.releasableReason === 'NO_HEARTBEAT') { const m = minutesAgoOf(job.lastActivityAt, now); return m !== null ? `không có tín hiệu ${m} phút` : 'không có tín hiệu'; }
  return null;
}
/** «hoạt động lần cuối 14:03:21 (2 phút trước)»; không có mốc ⇒ «chưa ghi nhận hoạt động». */
export function lastActivityLineOf(job: BlockingJob, now: number = Date.now()): string {
  const m = minutesAgoOf(job.lastActivityAt, now);
  if (m === null) return 'Chưa ghi nhận hoạt động nào của việc này.';
  return `Hoạt động lần cuối lúc ${new Date(job.lastActivityAt as string).toLocaleTimeString('vi-VN', { hour12: false })} (${m} phút trước).`;
}
/** Chữ mô tả dưới dòng việc chặn. */
export const blockingHintOf = (job: BlockingJob): string => (job.releasable
  ? `Việc này chặn thao tác ghi của plan${releaseReasonOf(job) ? ` (${releaseReasonOf(job)})` : ''}. Nếu đã treo thì gỡ để chạy tiếp; còn đang chạy thật thì đợi.`
  : 'Việc đang chạy bình thường — đợi nó xong rồi thử lại.');
export const releaseJobLinesOf = (job: BlockingJob, now: number = Date.now()): string[] => {
  const reason = releaseReasonOf(job, now);
  return [
    `Việc «${blockingNameOf(job)}» đã chạy ${job.runningMinutes} phút${job.maxMinutes > 0 ? ` (tối đa ${job.maxMinutes})` : ''}.`,
    ...(reason ? [`Lý do mời gỡ: ${reason}.`] : []),
    lastActivityLineOf(job, now),
    'Gỡ = đánh dấu việc này THẤT BẠI và trả lại khoá của plan; không gọi AI, không tốn tiền, nhưng kết quả dở dang của nó bị bỏ.',
    'Chỉ gỡ khi chắc việc đã chết. Việc còn chạy thật mà bị gỡ có thể ghi đè kết quả hoặc tốn tiền hai lần.',
  ];
};
/** Gỡ lỗi: 409 `NOT_RUNNING` = việc đã xong / đã gỡ (coi như thành công nhẹ). */
export const releaseNotRunningOf = (err: any): boolean => (err?.response?.data?.code ?? err?.response?.data?.errorCode) === 'NOT_RUNNING';
